/**
 * Delineation Intelligence Panel
 *
 * Carbon copy of Phreatic's WellInstallationPanel adapted for Polygon's
 * delineation-well (DW) recommendations. Same visual language — controls
 * row, 6 summary cards, satellite map with existing wells + recommended
 * DWs, two-column section with a Top Recommendations table + charts.
 *
 * Data source: polygonApi.getSentinelAlerts (the same source the 2D map
 * uses for its DW markers). This tab is additive — the 2D map markers
 * stay in place.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, ScatterChart, Scatter,
} from 'recharts';
import { MapContainer, CircleMarker, Rectangle, Tooltip as LTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { polygonApi, fetchDwNameOverrides, setDwNameOverride } from '../../api/polygon';
import { PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { EsriVectorBasemap } from '@/lib/basemap';
import { CtrlScrollZoom } from './CtrlScrollZoom';
import type { DWRecommendation, SentinelAlertsResponse } from '../../api/polygon';

interface DelineationIntelligencePanelProps {
  siteId: string;
  centerLat: number;
  centerLon: number;
  /** Existing site wells (extractedData.wells) for white-dot map markers + nearest-well lookup. */
  existingWells: { well_id: string; well_name?: string; latitude: number | null; longitude: number | null; total_depth_ft: number | null }[];
  /** Avg existing well depth — used to recommend per-DW install depth and tiered cost. */
  avgExistingWellDepthFt?: number;
  propertyBounds?: { north: number; south: number; east: number; west: number } | null;
  /** Active regulatory library slug. Sentinel-alerts refetched with this in
   *  the query string so DWs reflect the chosen MCLs. */
  regLibrary?: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#ef4444',
  high: '#f59e0b',
  moderate: '#06b6d4',
  // Polygon DWRecommendation also uses `priority` — keep both wired
  Critical: '#ef4444',
  High: '#f59e0b',
  Moderate: '#06b6d4',
};

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  moderate: 'Moderate',
};

const HORIZON_OPTIONS = [
  { value: 'now', label: 'Today' },
  { value: '6mo', label: '6 Months Forward' },
  { value: '1yr', label: '1 Year Forward' },
  { value: '2yr', label: '2 Years Forward' },
];

// Mirrors the depth-tier cost table used in the Cost Analysis card so the
// recommended DW install cost lines up across views.
function depthBasedCostPerWell(avgDepthFt: number): number {
  if (avgDepthFt <= 30) return 12_000;
  if (avgDepthFt <= 100) return 25_000;
  if (avgDepthFt <= 200) return 45_000;
  if (avgDepthFt <= 350) return 70_000;
  return 100_000;
}


function bearingCompass(deg: number): string {
  // Compact compass label: N, NE, E, SE, S, SW, W, NW
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'N'];
  const idx = Math.round(((deg % 360) + 360) % 360 / 45);
  return dirs[idx];
}

function distanceFt(lat1: number, lon1: number, lat2: number, lon2: number): number {
  // Quick local approximation. Same constants used in ThreePlumeMap.
  const FT_PER_DEG = 364000;
  const dLat = (lat2 - lat1) * FT_PER_DEG;
  const dLon = (lon2 - lon1) * FT_PER_DEG * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180);
  return Math.sqrt(dLat * dLat + dLon * dLon);
}

export function DelineationIntelligencePanel({
  siteId,
  centerLat,
  centerLon,
  existingWells,
  avgExistingWellDepthFt,
  propertyBounds,
  regLibrary,
}: DelineationIntelligencePanelProps) {
  const [data, setData] = useState<SentinelAlertsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [horizon, setHorizon] = useState<string>('now');
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [planning, setPlanning] = useState(false);
  const [plannedCount, setPlannedCount] = useState(0);

  // Per-DW custom name overrides keyed by trigger_well_id. Default labels stay
  // as `DW-{i+1}` — overrides take precedence at render time.
  const [dwOverrides, setDwOverrides] = useState<Record<string, string>>({});
  const [editingDwKey, setEditingDwKey] = useState<string | null>(null);
  const [editingDwValue, setEditingDwValue] = useState<string>('');

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchDwNameOverrides(siteId)
      .then(m => { if (!cancelled) setDwOverrides(m); })
      .catch(err => { if (!cancelled) console.warn('[POLYGON] DW name overrides fetch failed:', err); });
    return () => { cancelled = true; };
  }, [siteId]);

  /** Display label for a DW recommendation. Override > default `DW-{i+1}`. */
  const dwLabel = (r: { trigger_well_id: string }, i: number): string =>
    dwOverrides[r.trigger_well_id] ?? `DW-${i + 1}`;

  async function saveDwOverride(triggerWellId: string, newName: string) {
    try {
      const next = await setDwNameOverride(siteId, triggerWellId, newName);
      setDwOverrides(next);
      setEditingDwKey(null);
    } catch (err) {
      console.warn('[POLYGON] DW rename save failed:', err);
    }
  }

  const targetDate = useMemo(() => {
    const today = new Date();
    if (horizon === 'now') return today.toISOString().slice(0, 10);
    const map: Record<string, number> = { '6mo': 6, '1yr': 12, '2yr': 24 };
    const months = map[horizon] || 0;
    const d = new Date(today);
    d.setMonth(d.getMonth() + months);
    return d.toISOString().slice(0, 10);
  }, [horizon]);

  const runAnalysis = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await polygonApi.getSentinelAlerts(siteId, targetDate, regLibrary || undefined);
      setData(resp);
      setSelectedIdx(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load DW recommendations');
    } finally {
      setLoading(false);
    }
  };

  // Refetch when site, horizon, or reg library changes.
  useEffect(() => {
    runAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, regLibrary]);

  // Filter + sort. Single canonical ordering by combined_rank
  // (= 0.5 × flow_alignment + 0.5 × site-relative exceedance percentile),
  // computed server-side. Downgradient + worst-of-cohort trigger wells rank
  // first.
  const recommendations: DWRecommendation[] = useMemo(() => {
    const recs = data?.recommendations || [];
    const filtered = recs.filter(r =>
      r?.trigger_well_name && !/^(plume|recovery|mdpe|src|source)/i.test(r.trigger_well_name)
    );
    return [...filtered].sort((a, b) => (b.combined_rank ?? 0) - (a.combined_rank ?? 0));
  }, [data]);

  // Map a recommendation to its position in the site cohort by exceedance
  // (1 = worst-exceeding trigger well at the site).
  const exceedanceRankByIdx = useMemo(() => {
    const sortedByExc = [...recommendations].sort(
      (a, b) => (b.trigger_current_exceedance_ratio ?? 0) - (a.trigger_current_exceedance_ratio ?? 0),
    );
    const map = new Map<string, number>();
    sortedByExc.forEach((r, i) => map.set(r.trigger_well_id, i + 1));
    return map;
  }, [recommendations]);

  const recommendedDepthFt = useMemo(() => {
    if (avgExistingWellDepthFt && isFinite(avgExistingWellDepthFt) && avgExistingWellDepthFt > 0) {
      return Math.round(avgExistingWellDepthFt);
    }
    // Fallback: use median total_depth_ft of existing wells, else 100
    const depths = existingWells
      .map(w => w.total_depth_ft)
      .filter((d): d is number => typeof d === 'number' && isFinite(d) && d > 0)
      .sort((a, b) => a - b);
    if (depths.length === 0) return 100;
    return Math.round(depths[Math.floor(depths.length / 2)]);
  }, [avgExistingWellDepthFt, existingWells]);

  const costPerWell = useMemo(() => depthBasedCostPerWell(recommendedDepthFt), [recommendedDepthFt]);

  // For each DW, compute the nearest existing well distance (informational).
  const nearestExistingFt = useMemo(() => {
    const arr: number[] = [];
    for (const r of recommendations) {
      let best = Infinity;
      for (const w of existingWells) {
        if (w.latitude == null || w.longitude == null) continue;
        const d = distanceFt(r.recommended_lat, r.recommended_lon, w.latitude, w.longitude);
        if (d < best) best = d;
      }
      arr.push(isFinite(best) ? best : 0);
    }
    return arr;
  }, [recommendations, existingWells]);

  // Severity distribution — bar chart data
  const severityDistribution = useMemo(() => {
    const counts: Record<string, number> = { critical: 0, high: 0, moderate: 0 };
    for (const r of recommendations) {
      const key = (r.priority || '').toLowerCase();
      if (counts[key] !== undefined) counts[key] += 1;
      else counts.moderate += 1;
    }
    return Object.entries(counts).map(([sev, count]) => ({
      sev,
      count,
      label: SEVERITY_LABELS[sev] || sev,
      color: SEVERITY_COLORS[sev] || '#06b6d4',
    }));
  }, [recommendations]);

  // Summary metrics
  const summary = useMemo(() => {
    const total = recommendations.length;
    const critical = recommendations.filter(r => (r.priority || '').toLowerCase() === 'critical').length;
    const avgDistSource = total > 0
      ? Math.round(recommendations.reduce((s, r) => s + (r.distance_from_source_ft || 0), 0) / total)
      : 0;
    const avgDistTrigger = total > 0
      ? Math.round(recommendations.reduce((s, r) => s + (r.recommendation_distance_ft || 0), 0) / total)
      : 0;

    // Dominant bearing compass label
    const bearingBuckets: Record<string, number> = {};
    for (const r of recommendations) {
      const c = bearingCompass(r.bearing_deg || 0);
      bearingBuckets[c] = (bearingBuckets[c] || 0) + 1;
    }
    const dominantBearing = Object.entries(bearingBuckets).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

    const predictedCount = recommendations.filter(r => r.is_predicted).length;
    const measuredCount = total - predictedCount;
    return { total, critical, avgDistSource, avgDistTrigger, dominantBearing, predictedCount, measuredCount };
  }, [recommendations]);

  const planWell = async () => {
    if (selectedIdx == null || !recommendations[selectedIdx]) return;
    setPlanning(true);
    try {
      // Polygon-side persistence — placeholder for now. The endpoint shape can
      // mirror Phreatic's pump-control assets later; until then, simulate.
      await new Promise(r => setTimeout(r, 400));
      setPlannedCount(c => c + 1);
    } finally {
      setPlanning(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold text-mono-400 uppercase tracking-wider">
            Delineation Intelligence
          </h3>
          <button
            onClick={runAnalysis}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50"
          >
            {loading ? 'Analyzing...' : 'Run Analysis'}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Target Horizon</label>
            <select
              value={horizon}
              onChange={e => setHorizon(e.target.value)}
              className="w-full mt-0.5 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200 focus:border-cyan-500 focus:outline-none"
            >
              {HORIZON_OPTIONS.map(h => (
                <option key={h.value} value={h.value}>{h.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Recommended Depth</label>
            <div className="mt-0.5 px-2 py-1 text-xs bg-mono-800/50 border border-mono-700 rounded text-cyan-400">
              {recommendedDepthFt} ft (avg of existing)
            </div>
          </div>
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Cost / Well</label>
            <div className="mt-0.5 px-2 py-1 text-xs bg-mono-800/50 border border-mono-700 rounded text-mono-300">
              ${costPerWell.toLocaleString()}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-mono-500 uppercase tracking-wider">Existing Wells</label>
            <div className="mt-0.5 px-2 py-1 text-xs bg-mono-800/50 border border-mono-700 rounded text-mono-300">
              {existingWells.length} wells
            </div>
          </div>
        </div>
        <div className="mt-2 text-[10px] text-mono-500">
          Ranked by <span className="text-cyan-400">combined score</span> = flow direction (50%) + site-relative exceedance (50%).
          Downgradient triggers near the worst-exceeding wells at this site appear first.
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-32 bg-mono-900 border border-mono-800 rounded-lg">
          <div className="text-mono-400 text-sm animate-pulse">Computing delineation recommendations...</div>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <SummaryCard
              label="Recommended DWs"
              value={`${summary.total}`}
              sub={`for ${HORIZON_OPTIONS.find(h => h.value === horizon)?.label}`}
              color="cyan"
            />
            <SummaryCard
              label="Critical Priority"
              value={`${summary.critical}`}
              sub={summary.total > 0 ? `${Math.round(summary.critical / summary.total * 100)}% of recs` : '—'}
              color={summary.critical > 0 ? 'red' : 'green'}
            />
            <SummaryCard
              label="Avg Distance from Source"
              value={`${summary.avgDistSource.toLocaleString()} ft`}
              sub="from plume center"
              color="cyan"
            />
            <SummaryCard
              label="Avg Distance from Trigger"
              value={`${summary.avgDistTrigger.toLocaleString()} ft`}
              sub="between trigger + DW"
              color="cyan"
            />
            <SummaryCard
              label="Dominant Bearing"
              value={summary.dominantBearing}
              sub="downgradient direction"
              color="cyan"
            />
            <SummaryCard
              label="Predicted vs Measured"
              value={`${summary.predictedCount} / ${summary.measuredCount}`}
              sub="model vs lab triggers"
              color="amber"
            />
          </div>

          {/* Satellite Map with Recommended DWs */}
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
              Property — Recommended Delineation Wells
            </h3>
            <div className="rounded-lg overflow-hidden" style={{ height: 400 }}>
              <MapContainer
                key={`dw-map-${recommendations.length}-${horizon}`}
                center={[centerLat, centerLon]}
                zoom={14}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <CtrlScrollZoom />
                <EsriVectorBasemap />
                {/* Property boundary — real bounds if available, else 640 acres default */}
                {(() => {
                  let bounds: [[number, number], [number, number]];
                  if (propertyBounds) {
                    bounds = [
                      [propertyBounds.south, propertyBounds.west],
                      [propertyBounds.north, propertyBounds.east],
                    ];
                  } else {
                    const sideFt = Math.sqrt(640 * 43560);
                    const halfLat = (sideFt / 2) / 364000;
                    const halfLon = (sideFt / 2) / (364000 * Math.cos(centerLat * Math.PI / 180));
                    bounds = [
                      [centerLat - halfLat, centerLon - halfLon],
                      [centerLat + halfLat, centerLon + halfLon],
                    ];
                  }
                  return (
                    <Rectangle bounds={bounds} pathOptions={{ color: '#06b6d4', weight: 2, fillOpacity: 0.05, dashArray: '6 4' }} />
                  );
                })()}
                {/* Existing wells — white dots */}
                {existingWells.map(w => (
                  w.latitude != null && w.longitude != null && (
                    <CircleMarker
                      key={w.well_id}
                      center={[w.latitude, w.longitude]}
                      radius={4}
                      pathOptions={{ color: '#fff', fillColor: '#fff', fillOpacity: 0.9, weight: 1 }}
                    >
                      <LTooltip direction="top" offset={[0, -6]}>
                        <span style={{ fontSize: 11 }}>{w.well_name || w.well_id} (existing)</span>
                      </LTooltip>
                    </CircleMarker>
                  )
                ))}
                {/* Plume source marker */}
                <CircleMarker
                  center={[centerLat, centerLon]}
                  radius={6}
                  pathOptions={{ color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.85, weight: 1 }}
                >
                  <LTooltip direction="top" offset={[0, -8]}>
                    <span style={{ fontSize: 11 }}>Plume Source</span>
                  </LTooltip>
                </CircleMarker>
                {/* Recommended DWs — numbered colored circles by priority */}
                {recommendations.map((r, i) => {
                  const sev = (r.priority || 'moderate').toLowerCase();
                  const color = SEVERITY_COLORS[sev] || '#06b6d4';
                  const isSelected = selectedIdx === i;
                  return (
                    <CircleMarker
                      key={`dw-${i}`}
                      center={[r.recommended_lat, r.recommended_lon]}
                      radius={isSelected ? 10 : 7}
                      pathOptions={{
                        color: isSelected ? '#fff' : '#06b6d4',
                        fillColor: color,
                        fillOpacity: 0.8,
                        weight: isSelected ? 3 : 2,
                      }}
                      eventHandlers={{ click: () => setSelectedIdx(isSelected ? null : i) }}
                    >
                      <LTooltip direction="top" offset={[0, -8]} permanent={i < 3}>
                        <span style={{ fontSize: 10, fontWeight: 600 }}>
                          {dwLabel(r, i)} — {SEVERITY_LABELS[sev] || sev} • {Math.round(r.distance_from_source_ft)} ft
                        </span>
                      </LTooltip>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            </div>
            <div className="flex items-center gap-4 mt-2 text-[9px] text-mono-400">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-white border border-mono-600" /> Existing Well
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#f59e0b' }} /> Plume Source
              </span>
              {Object.entries(SEVERITY_LABELS).map(([sev, lbl]) => (
                <span key={sev} className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full border border-cyan-400" style={{ backgroundColor: SEVERITY_COLORS[sev], opacity: 0.85 }} />
                  {lbl}
                </span>
              ))}
              <span className="flex items-center gap-1">
                <span className="w-4 h-2 border border-cyan-400 border-dashed" style={{ opacity: 0.5 }} /> Property
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Recommended DW Table */}
            <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
                Recommended Delineation Wells
              </h3>
              {recommendations.length === 0 ? (
                <div className="text-mono-500 text-xs">No delineation wells recommended for this horizon.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-mono-400 border-b border-mono-800">
                        <th className="text-left py-1.5 px-1.5">#</th>
                        <th className="text-left py-1.5 px-1.5">Lat / Lon</th>
                        <th className="text-right py-1.5 px-1.5">From Source</th>
                        <th className="text-right py-1.5 px-1.5">Bearing</th>
                        <th className="text-left py-1.5 px-1.5">Trigger</th>
                        <th className="text-right py-1.5 px-1.5">Exceedance vs Site</th>
                        <th className="text-right py-1.5 px-1.5">Rank</th>
                        <th className="text-center py-1.5 px-1.5">Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recommendations.map((r, i) => {
                        const sev = (r.priority || 'moderate').toLowerCase();
                        return (
                          <tr
                            key={i}
                            className={`border-b border-mono-800/50 text-mono-300 cursor-pointer transition-colors ${selectedIdx === i ? 'bg-cyan-500/10' : 'hover:bg-mono-800/50'}`}
                            onClick={() => setSelectedIdx(selectedIdx === i ? null : i)}
                          >
                            <td className="py-1.5 px-1.5 font-medium text-mono-400" onClick={(e) => editingDwKey === r.trigger_well_id ? e.stopPropagation() : undefined}>
                              {editingDwKey === r.trigger_well_id ? (
                                <span className="inline-flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={editingDwValue}
                                    onChange={(e) => setEditingDwValue(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveDwOverride(r.trigger_well_id, editingDwValue);
                                      if (e.key === 'Escape') setEditingDwKey(null);
                                    }}
                                    autoFocus
                                    className="w-20 px-1 py-0 text-[10px] bg-mono-800 border border-mono-600 rounded text-mono-100"
                                  />
                                  <button onClick={(e) => { e.stopPropagation(); saveDwOverride(r.trigger_well_id, editingDwValue); }} className="text-emerald-400 hover:text-emerald-300">
                                    <CheckIcon className="h-3 w-3" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setEditingDwKey(null); }} className="text-mono-500 hover:text-mono-300">
                                    <XMarkIcon className="h-3 w-3" />
                                  </button>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 group/dw">
                                  <span>{dwLabel(r, i)}</span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setEditingDwKey(r.trigger_well_id); setEditingDwValue(dwLabel(r, i)); }}
                                    className="opacity-0 group-hover/dw:opacity-100 text-mono-500 hover:text-mono-300 transition-opacity"
                                    title="Rename"
                                  >
                                    <PencilSquareIcon className="h-3 w-3" />
                                  </button>
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 px-1.5 text-cyan-300 font-mono text-[10px]">
                              {r.recommended_lat.toFixed(5)}, {r.recommended_lon.toFixed(5)}
                            </td>
                            <td className="text-right py-1.5 px-1.5">{Math.round(r.distance_from_source_ft).toLocaleString()} ft</td>
                            <td className="text-right py-1.5 px-1.5">{Math.round(r.bearing_deg)}° {bearingCompass(r.bearing_deg)}</td>
                            <td className="py-1.5 px-1.5 text-mono-400 truncate" style={{ maxWidth: 110 }} title={r.trigger_well_name}>{r.trigger_well_name}</td>
                            <td className="text-right py-1.5 px-1.5">
                              {(() => {
                                const ratio = r.trigger_current_exceedance_ratio ?? 0;
                                const rank = exceedanceRankByIdx.get(r.trigger_well_id);
                                return (
                                  <span
                                    className={
                                      ratio >= 5 ? 'text-red-400 font-semibold'
                                      : ratio >= 2 ? 'text-amber-400 font-semibold'
                                      : 'text-mono-400'
                                    }
                                  >
                                    {ratio > 0 ? `${ratio.toFixed(1)}× MCL` : '—'}
                                    {rank && recommendations.length > 1 && (
                                      <span className="text-mono-600 text-[9px] ml-1">#{rank}/{recommendations.length}</span>
                                    )}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="text-right py-1.5 px-1.5">
                              {(() => {
                                const rk = r.combined_rank ?? 0;
                                return (
                                  <span
                                    className={
                                      rk >= 0.7 ? 'text-red-400 font-semibold'
                                      : rk >= 0.4 ? 'text-amber-400 font-semibold'
                                      : 'text-cyan-300'
                                    }
                                    title={
                                      `Flow alignment: ${((r.flow_alignment_score ?? 0) * 100).toFixed(0)}%\n` +
                                      `Site-relative exceedance percentile: ${((r.relative_exceedance_rank ?? 0) * 100).toFixed(0)}%`
                                    }
                                  >
                                    {rk.toFixed(2)}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="text-center py-1.5 px-1.5">
                              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: SEVERITY_COLORS[sev] || '#06b6d4' }} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Selected DW detail */}
              {selectedIdx != null && recommendations[selectedIdx] && (
                <div className="mt-3 bg-mono-800/50 rounded-lg p-3 border border-cyan-500/20">
                  {(() => {
                    const r = recommendations[selectedIdx];
                    const sev = (r.priority || 'moderate').toLowerCase();
                    const nearest = nearestExistingFt[selectedIdx] || 0;
                    return (
                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-cyan-300">{dwLabel(r, selectedIdx)} Detail</div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                          <div className="text-mono-500">Coordinates</div>
                          <div className="text-mono-300 font-mono">{r.recommended_lat.toFixed(6)}, {r.recommended_lon.toFixed(6)}</div>
                          <div className="text-mono-500">Distance from Source</div>
                          <div className="text-mono-300">{Math.round(r.distance_from_source_ft).toLocaleString()} ft</div>
                          <div className="text-mono-500">Bearing</div>
                          <div className="text-mono-300">{Math.round(r.bearing_deg)}° ({bearingCompass(r.bearing_deg)})</div>
                          <div className="text-mono-500">Distance from Trigger Well</div>
                          <div className="text-mono-300">{Math.round(r.recommendation_distance_ft).toLocaleString()} ft</div>
                          <div className="text-mono-500">Trigger Well</div>
                          <div className="text-mono-300">{r.trigger_well_name}</div>
                          <div className="text-mono-500">Trigger Compounds</div>
                          <div className="text-mono-300">{r.trigger_compounds.join(', ') || '—'}</div>
                          <div className="text-mono-500">Source</div>
                          <div className="text-mono-300">{r.is_predicted ? 'Predicted (model)' : 'Measured (lab)'}</div>
                          <div className="text-mono-500">Projected Conc.</div>
                          <div className="text-mono-300">{r.projected_concentration_ugl ? `${r.projected_concentration_ugl.toFixed(1)} µg/L` : '—'}</div>
                          <div className="text-mono-500">Nearest Existing Well</div>
                          <div className="text-mono-300">{nearest.toLocaleString()} ft</div>
                          <div className="text-mono-500">Recommended Depth</div>
                          <div className="text-cyan-400 font-semibold">{recommendedDepthFt} ft</div>
                          <div className="text-mono-500">Estimated Install Cost</div>
                          <div className="text-emerald-400 font-semibold">${costPerWell.toLocaleString()}</div>
                          <div className="text-mono-500">Trigger Current Exceedance</div>
                          <div className="text-mono-300">
                            {(r.trigger_current_exceedance_ratio ?? 0).toFixed(1)}× MCL
                          </div>
                          <div className="text-mono-500">Site-Relative Exceedance</div>
                          <div className="text-mono-300">
                            {(() => {
                              const rk = exceedanceRankByIdx.get(r.trigger_well_id);
                              return rk
                                ? `#${rk} of ${recommendations.length} (${((r.relative_exceedance_rank ?? 0) * 100).toFixed(0)}th percentile)`
                                : '—';
                            })()}
                          </div>
                          <div className="text-mono-500">Flow Alignment</div>
                          <div className="text-mono-300">{((r.flow_alignment_score ?? 0) * 100).toFixed(0)}% (downgradient = 100%)</div>
                          <div className="text-mono-500">Combined Rank</div>
                          <div className="text-cyan-400 font-semibold">{(r.combined_rank ?? 0).toFixed(2)} / 1.00</div>
                        </div>
                        <div className="border-t border-mono-700 pt-2 mt-2">
                          <div className="text-[10px] text-mono-500 uppercase tracking-wider mb-1">Rationale</div>
                          <div className="text-xs text-mono-200">{r.rationale}</div>
                          <button
                            onClick={planWell}
                            disabled={planning}
                            className="mt-2 px-3 py-1.5 text-xs rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 disabled:opacity-50"
                          >
                            {planning ? 'Saving...' : `Plan Well DW-${plannedCount + 1} at This Location`}
                          </button>
                          <div className="text-[9px] text-mono-600 mt-1">
                            Priority <span style={{ color: SEVERITY_COLORS[sev] }}>{SEVERITY_LABELS[sev] || sev}</span> · {r.is_predicted ? 'forward-looking' : 'lab-confirmed'}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Right column: priority distribution + bearing scatter */}
            <div className="space-y-3">
              <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
                  Priority Distribution
                </h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={severityDistribution} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#888', fontSize: 10 }} />
                    <YAxis dataKey="label" type="category" tick={{ fill: '#888', fontSize: 10 }} width={80} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11, borderRadius: 6 }}
                      formatter={(val: number) => [`${val} wells`, 'Count']}
                    />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {severityDistribution.map((d, i) => (
                        <Cell key={i} fill={d.color} fillOpacity={0.75} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2 text-[9px] text-mono-400">
                  {Object.entries(SEVERITY_LABELS).map(([sev, lbl]) => (
                    <span key={sev} className="flex items-center gap-1">
                      <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SEVERITY_COLORS[sev], opacity: 0.75 }} />
                      {lbl}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
                  Distance from Source vs Bearing
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <ScatterChart margin={{ top: 5, right: 10, bottom: 25, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis
                      dataKey="bearing_deg" type="number" name="Bearing" domain={[0, 360]}
                      tick={{ fill: '#888', fontSize: 10 }}
                      label={{ value: 'Bearing (°)', position: 'insideBottom', offset: -15, fill: '#666', fontSize: 10 }}
                    />
                    <YAxis
                      dataKey="distance_from_source_ft" type="number" name="Distance"
                      tick={{ fill: '#888', fontSize: 10 }}
                      label={{ value: 'Distance (ft)', angle: -90, position: 'insideLeft', fill: '#666', fontSize: 10 }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11, borderRadius: 6 }}
                      formatter={(val: number, name: string) => [
                        `${val}${name === 'Bearing' ? '°' : ' ft'}`,
                        name,
                      ]}
                    />
                    <Scatter data={recommendations as unknown as any[]} fill="#06b6d4" fillOpacity={0.8}>
                      {recommendations.map((r, i) => (
                        <Cell key={i} fill={SEVERITY_COLORS[(r.priority || 'moderate').toLowerCase()] || '#06b6d4'} />
                      ))}
                    </Scatter>
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Total install cost rollup */}
          {recommendations.length > 0 && (
            <div className="bg-mono-900 border border-emerald-500/20 rounded-lg p-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] text-emerald-300 uppercase tracking-wider">Total Estimated Install</div>
                <div className="text-mono-200 text-sm mt-0.5">
                  {recommendations.length} wells × ${costPerWell.toLocaleString()} per well (at {recommendedDepthFt} ft)
                </div>
              </div>
              <div className="text-lg font-bold text-emerald-400">
                ${(recommendations.length * costPerWell).toLocaleString()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, color }: { label: string; value: string; sub: string; color: string }) {
  const colorMap: Record<string, string> = {
    cyan: 'text-cyan-400 border-cyan-500/30',
    green: 'text-emerald-400 border-emerald-500/30',
    amber: 'text-amber-400 border-amber-500/30',
    red: 'text-red-400 border-red-500/30',
  };
  const c = colorMap[color] || colorMap.cyan;
  return (
    <div className={`bg-mono-900 border rounded-lg p-2 ${c.split(' ')[1]}`}>
      <div className="text-[9px] text-mono-500 uppercase tracking-wider">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${c.split(' ')[0]}`}>{value}</div>
      <div className="text-[9px] text-mono-600">{sub}</div>
    </div>
  );
}
